import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Button,
  Space,
  Tag,
  Collapse,
  Switch,
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  ClearOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { RangePickerProps } from 'antd/es/date-picker';
import moment from 'moment';

import styles from '../../pages/admin/Subscribers.module.css';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Panel } = Collapse;

export interface FilterConfig {
  key: string;
  label: string;
  type: 'text' | 'select' | 'multiSelect' | 'dateRange' | 'numberRange' | 'boolean';
  placeholder?: string;
  options?: { value: any; label: string; color?: string }[];
  width?: number;
}

export interface FilterValues {
  [key: string]: any;
}

interface TableFilterProps {
  filters: FilterConfig[];
  onFilterChange: (filters: FilterValues) => void;
  onClearFilters: () => void;
  initialFilters?: FilterValues;
  showFilterCount?: boolean;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

const TableFilter: React.FC<TableFilterProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
  initialFilters = {},
  showFilterCount = true,
  collapsible = true,
  defaultCollapsed = false,
}) => {
  const [filterValues, setFilterValues] = useState<FilterValues>(initialFilters);
  const [isExpanded, setIsExpanded] = useState(!defaultCollapsed);
  const { t } = useTranslation();

  // Ajout d'une key pour forcer le re-render des inputs
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    if (filterValues && Object.keys(filterValues).length > 0)
      onFilterChange(filterValues);
  }, [filterValues, onFilterChange]);

  const handleFilterChange = (key: string, value: any) => {
    setFilterValues(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilterValues({});
    setResetKey(prev => prev + 1); // Force le re-render des inputs
    onClearFilters();
  };

  const getActiveFilterCount = () => {
    return Object.values(filterValues).filter(value => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== undefined && value !== '';
    }).length;
  };

  const renderFilterControl = (filter: FilterConfig) => {
    const value = filterValues[filter.key];
    
    switch (filter.type) {
      case 'text':
        return (
          <Input
            key={`${filter.key}-${resetKey}`} // Force le re-render quand resetKey change
            placeholder={filter.placeholder || t('filters.searchPlaceholder', { field: filter.label.toLowerCase() })}
            defaultValue={value || ''} // Utilise defaultValue au lieu de value
            onBlur={(e) => {
              handleFilterChange(filter.key, e.target.value);
            }}
            onPressEnter={(e) => {
              handleFilterChange(filter.key, e.currentTarget.value);
            }}
            onChange={(e) => {
              // Détecte quand le champ est vidé via le bouton X
              if (e.target.value === '') {
                handleFilterChange(filter.key, '');
              }
            }}
            allowClear
            className={styles.customInput}
          />
        );

      case 'select':
        return (
          <Select
            key={`${filter.key}-${resetKey}`}
            placeholder={filter.placeholder || t('filters.selectPlaceholder', { field: filter.label.toLowerCase() })}
            value={value}
            onChange={(val) => handleFilterChange(filter.key, val)}
            allowClear
            className={styles.customSelect}
            style={{ width: '100%' }}
          >
            {filter.options?.map(option => (
              <Option key={option.value} value={option.value}>
                {option.color ? (
                  <Tag color={option.color}>{option.label}</Tag>
                ) : (
                  option.label
                )}
              </Option>
            ))}
          </Select>
        );

      case 'multiSelect':
        return (
          <Select
            key={`${filter.key}-${resetKey}`}
            mode="multiple"
            placeholder={filter.placeholder || t('filters.selectPlaceholder', { field: filter.label.toLowerCase() })}
            value={value || []}
            onChange={(val) => handleFilterChange(filter.key, val)}
            allowClear
            style={{ width: '100%' }}
            className={styles.customMultiselect}
          >
            {filter.options?.map(option => (
              <Option key={option.value} value={option.value}>
                {option.color ? (
                  <Tag color={option.color}>{option.label}</Tag>
                ) : (
                  option.label
                )}
              </Option>
            ))}
          </Select>
        );

      case 'dateRange':
        return (
          <RangePicker
            key={`${filter.key}-${resetKey}`}
            style={{ width: '100%' }}
            className={styles.customDateRange}
            placeholder={[t('filters.dateStart'), t('filters.dateEnd')]}
            value={value ? [moment(value[0]), moment(value[1])] : null}
            onChange={(dates, dateStrings) => {
              if (dates) {
                handleFilterChange(filter.key, [dates[0]?.toDate(), dates[1]?.toDate()]);
              } else {
                handleFilterChange(filter.key, null);
              }
            }}
            format="DD/MM/YYYY"
          />
        );

      case 'numberRange':
        return (
          <Input.Group
            key={`${filter.key}-${resetKey}`}
            compact
            onBlur={(e) => {
              setTimeout(() => {
                const minInput = document.getElementById(`number-range-min-${filter.key}`) as HTMLInputElement;
                const maxInput = document.getElementById(`number-range-max-${filter.key}`) as HTMLInputElement;
                
                if (!e.currentTarget?.contains(e.relatedTarget as Element)) {
                  const minValue = minInput?.value ?? undefined;
                  const maxValue = maxInput?.value ?? undefined;
                  handleFilterChange(filter.key, [
                    minValue ? parseFloat(minValue) : undefined,
                    maxValue ? parseFloat(maxValue) : undefined
                  ]);
                }
              }, 0);
            }}
          >
            <InputNumber
              id={`number-range-min-${filter.key}`}
              style={{ width: '50%' }}
              placeholder={t('filters.minValue')}
              defaultValue={value?.[0]}
              className={styles.customInputNumber}
            />
            <InputNumber
              id={`number-range-max-${filter.key}`}
              style={{ width: '50%' }}
              placeholder={t('filters.maxValue')}
              defaultValue={value?.[1]}
              className={styles.customInputNumber}
            />
          </Input.Group>
        );

      case 'boolean':
        return (
          <Switch
            key={`${filter.key}-${resetKey}`}
            checked={value}
            onChange={(checked) => handleFilterChange(filter.key, checked)}
            checkedChildren={t('common.yes')}
            unCheckedChildren={t('common.no')}
          />
        );

      default:
        return null;
    }
  };

  const FilterContent = () => (
    <div style={{ padding: '16px 0' }}>
      <Row gutter={[16, 16]}>
        {filters.map(filter => (
          <Col
            key={filter.key}
            xs={24}
            sm={12}
            md={filter.width || 8}
            lg={filter.width || 6}
          >
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                {filter.label}
              </label>
              {renderFilterControl(filter)}
            </div>
          </Col>
        ))}
        <Col xs={24} sm={12} md={8} lg={6}>
          <div style={{ paddingTop: '24px' }}>
            <Space>
              <Button
                icon={<ClearOutlined />}
                onClick={handleClearFilters}
                disabled={getActiveFilterCount() === 0}
              >
                {t('filters.clear')}
              </Button>
              {showFilterCount && getActiveFilterCount() > 0 && (
                <Tag color="blue">{getActiveFilterCount()} {t('filters.activeFilters')}</Tag>
              )}
            </Space>
          </div>
        </Col>
      </Row>
    </div>
  );

  if (collapsible) {
    return (
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title={
          <Space>
            <FilterOutlined />
            {t('filters.title')}
            {showFilterCount && getActiveFilterCount() > 0 && (
              <Tag color="blue">{getActiveFilterCount()}</Tag>
            )}
          </Space>
        }
        extra={
          <Button
            type="text"
            icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? t('filters.collapse') : t('filters.expand')}
          </Button>
        }
      >
        <Collapse
          activeKey={isExpanded ? ['filters'] : []}
          onChange={() => setIsExpanded(!isExpanded)}
          ghost
        >
          <Panel header="" key="filters" showArrow={false}>
            <FilterContent />
          </Panel>
        </Collapse>
      </Card>
    );
  }

  return (
    <Card
      size="small"
      style={{ marginBottom: 16 }}
      title={
        <Space>
          <FilterOutlined />
          {t('filters.title')}
          {showFilterCount && getActiveFilterCount() > 0 && (
            <Tag color="blue">{getActiveFilterCount()}</Tag>
          )}
        </Space>
      }
    >
      <FilterContent />
    </Card>
  );
};

export default TableFilter;